import { adminApi } from "./adminApi";

const DEFAULT_PAGINATION = Object.freeze({
  currentPage: 1,
  limit: 10,
  totalCount: 0,
  totalPages: 1,
  hasMore: false,
});

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );

export const whiteLabelApi = adminApi.injectEndpoints({
  endpoints: (builder) => ({
    getWhiteLabelCompanies: builder.query({
      query: (params = {}) => ({
        url: "/white-label/companies",
        params: cleanParams(params),
      }),
      transformResponse: (response) => ({
        items: Array.isArray(response?.data) ? response.data : [],
        pagination: response?.pagination || DEFAULT_PAGINATION,
      }),
      providesTags: (result) =>
        result?.items?.length
          ? [
              { type: "WhiteLabelCompany", id: "LIST" },
              ...result.items.map((company) => ({
                type: "WhiteLabelCompany",
                id: company?._id,
              })),
            ]
          : [{ type: "WhiteLabelCompany", id: "LIST" }],
    }),
    getWhiteLabelCompany: builder.query({
      query: (companyId) => `/white-label/companies/${companyId}`,
      transformResponse: (response) => response?.data || null,
      providesTags: (_result, _error, companyId) => [
        { type: "WhiteLabelCompany", id: companyId },
      ],
    }),
    getWhiteLabelCompanyRuntimeConfig: builder.query({
      query: (companyId) => `/white-label/company/${companyId}/config`,
      transformResponse: (response) => response?.data || null,
      providesTags: (_result, _error, companyId) => [
        { type: "WhiteLabelCompany", id: `RUNTIME:${companyId}` },
      ],
    }),
    getWhiteLabelBrandingByCompany: builder.query({
      query: (companyId) => `/white-label/branding/${companyId}`,
      transformResponse: (response) => response?.data || null,
      providesTags: (_result, _error, companyId) => [
        { type: "WhiteLabelBranding", id: companyId },
      ],
    }),
    createWhiteLabelBranding: builder.mutation({
      query: (body) => ({
        url: "/white-label/branding",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "WhiteLabelBranding", id: arg.companyId },
      ],
    }),
    updateWhiteLabelBranding: builder.mutation({
      query: ({ companyId, ...body }) => ({
        url: `/white-label/branding/${companyId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "WhiteLabelBranding", id: arg.companyId },
      ],
    }),
    getWhiteLabelThemeByCompany: builder.query({
      query: (companyId) => `/white-label/themes/${companyId}`,
      transformResponse: (response) => response?.data || null,
      providesTags: (_result, _error, companyId) => [
        { type: "WhiteLabelTheme", id: companyId },
      ],
    }),
    createWhiteLabelTheme: builder.mutation({
      query: (body) => ({
        url: "/white-label/themes",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "WhiteLabelTheme", id: arg.companyId },
      ],
    }),
    updateWhiteLabelTheme: builder.mutation({
      query: ({ companyId, ...body }) => ({
        url: `/white-label/themes/${companyId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "WhiteLabelTheme", id: arg.companyId },
      ],
    }),
    getWhiteLabelDomainsByCompany: builder.query({
      query: (companyId) => `/white-label/domains/company/${companyId}`,
      transformResponse: (response) =>
        Array.isArray(response?.data) ? response.data : [],
      providesTags: (result, _error, companyId) =>
        result?.length
          ? [
              { type: "WhiteLabelDomain", id: `COMPANY:${companyId}` },
              ...result.map((item) => ({
                type: "WhiteLabelDomain",
                id: item?._id,
              })),
            ]
          : [{ type: "WhiteLabelDomain", id: `COMPANY:${companyId}` }],
    }),
    getWhiteLabelDomain: builder.query({
      query: (domainId) => `/white-label/domains/${domainId}`,
      transformResponse: (response) => response?.data || null,
      providesTags: (_result, _error, domainId) => [
        { type: "WhiteLabelDomain", id: domainId },
      ],
    }),
    createWhiteLabelDomain: builder.mutation({
      query: (body) => ({
        url: "/white-label/domains",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "WhiteLabelDomain", id: `COMPANY:${arg.companyId}` },
      ],
    }),
    updateWhiteLabelDomain: builder.mutation({
      query: ({ domainId, ...body }) => ({
        url: `/white-label/domains/${domainId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "WhiteLabelDomain", id: arg.domainId },
        { type: "WhiteLabelDomain", id: `COMPANY:${arg.companyId}` },
      ],
    }),
    getWhiteLabelWebsiteByCompany: builder.query({
      query: (companyId) => `/white-label/websites/${companyId}`,
      transformResponse: (response) => response?.data || null,
      providesTags: (_result, _error, companyId) => [
        { type: "WhiteLabelWebsite", id: companyId },
      ],
    }),
    createWhiteLabelWebsite: builder.mutation({
      query: (body) => ({
        url: "/white-label/websites",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "WhiteLabelWebsite", id: arg.companyId },
      ],
    }),
    updateWhiteLabelWebsite: builder.mutation({
      query: ({ companyId, ...body }) => ({
        url: `/white-label/websites/${companyId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "WhiteLabelWebsite", id: arg.companyId },
      ],
    }),
    createWhiteLabelCompany: builder.mutation({
      query: (body) => ({
        url: "/white-label/companies",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "WhiteLabelCompany", id: "LIST" }],
    }),
    updateWhiteLabelCompany: builder.mutation({
      query: ({ companyId, ...body }) => ({
        url: `/white-label/companies/${companyId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "WhiteLabelCompany", id: "LIST" },
        { type: "WhiteLabelCompany", id: arg.companyId },
      ],
    }),
    deleteWhiteLabelCompany: builder.mutation({
      query: (companyId) => ({
        url: `/white-label/companies/${companyId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, companyId) => [
        { type: "WhiteLabelCompany", id: "LIST" },
        { type: "WhiteLabelCompany", id: companyId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetWhiteLabelCompaniesQuery,
  useGetWhiteLabelCompanyQuery,
  useGetWhiteLabelCompanyRuntimeConfigQuery,
  useGetWhiteLabelBrandingByCompanyQuery,
  useCreateWhiteLabelBrandingMutation,
  useUpdateWhiteLabelBrandingMutation,
  useGetWhiteLabelThemeByCompanyQuery,
  useCreateWhiteLabelThemeMutation,
  useUpdateWhiteLabelThemeMutation,
  useGetWhiteLabelDomainsByCompanyQuery,
  useGetWhiteLabelDomainQuery,
  useCreateWhiteLabelDomainMutation,
  useUpdateWhiteLabelDomainMutation,
  useGetWhiteLabelWebsiteByCompanyQuery,
  useCreateWhiteLabelWebsiteMutation,
  useUpdateWhiteLabelWebsiteMutation,
  useCreateWhiteLabelCompanyMutation,
  useUpdateWhiteLabelCompanyMutation,
  useDeleteWhiteLabelCompanyMutation,
} = whiteLabelApi;
